import React from "react";
import Modal from "common/Modal/Modal";
import { useRouteMatch } from "react-router-dom";
import { TRADE_EXTRA_DETAILS_PATH } from "common/constants";
import { getRouteWithoutPath } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { useCancelTradeModalContent } from "features/trades/CancelTrade/CancelTradeModalContent";
import { LanguageNameSpaces, TradeData } from "@shared/types";
import { useCancelTradeModal } from "@shared/features/trades/common/hooks";
import { displayResultMessage } from "utils/misc";
import { BulkCloseTradesStep } from "@shared/features/trades/common/types";
import InactiveAccountWarning from "features/trades/CancelTrade/InactiveAccountWarning";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

interface CancelTradeModalProps {
  tradeId?: number;
  tradeData?: TradeData;
  closeModal: () => void;
  showCloseOptions?: boolean;
  isVisible?: boolean;
  accountIsValid?: boolean;
  symbol?: string;
  accountId?: number;
}

const CancelTradeModal = ({
  tradeData,
  tradeId: propsTradeId,
  closeModal,
  showCloseOptions: propsShowCloseOptions,
  isVisible = true,
  accountIsValid,
  symbol,
  accountId,
}: CancelTradeModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { historyPush } = useHistoryWithQuery();
  const tradesExtraDetailsMatch = Boolean(
    useRouteMatch(TRADE_EXTRA_DETAILS_PATH)
  );
  const { showCloseOptions, isLoading, onCancel, onCancelTrade, step } =
    useCancelTradeModal({
      displayResultMessage,
      tradeData,
      tradeId: propsTradeId,
      closeModal,
      showCloseOptions: propsShowCloseOptions,
      onSuccess: () => {
        if (tradesExtraDetailsMatch) {
          historyPush(
            getRouteWithoutPath(location.pathname, TRADE_EXTRA_DETAILS_PATH)
          );
        }
      },
      isVisible,
      accountIsValid,
      symbol,
      accountId,
    });
  const { sellCoins, cancelTradeModalContent } = useCancelTradeModalContent(
    showCloseOptions,
    symbol,
    accountId
  );

  return (
    <Modal
      onCancel={onCancel}
      open={isVisible}
      title={t("actions.close_trade")}
      onOk={() => onCancelTrade(sellCoins)}
      okButtonProps={{ loading: isLoading }}
      destroyOnHidden={true}
    >
      {step === BulkCloseTradesStep.CONFIRM_INACTIVE_ACCOUNT ? (
        <InactiveAccountWarning />
      ) : (
        cancelTradeModalContent
      )}
    </Modal>
  );
};

export default React.memo(CancelTradeModal);
